import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'
import { Button, FullPageOverlay } from '@fs/zion-ui'
import { MultiImageViewer } from '@fs/zion-image-viewer'
import { NoticeLoading } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'

const FADE = 'opacity 0.4s ease-out'

// The dark backdrop the old memories gallery had, behind the thumbnail and the photo alike
const stageCss = css`
  position: relative;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.75);
`

// The blurred thumbnail sits behind the viewer while the full image loads, so the
// frame is never empty, then fades out as the full image fades in over it.
const thumbnailCss = css`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: blur(8px);
  transition: ${FADE};

  &[data-hidden='true'] {
    opacity: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const spinnerCss = css`
  position: absolute;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  pointer-events: none;
`

// The viewer draws a gray skeleton in place of its stage until the image is in, which
// would cover the thumbnail. Only the stage is held back (the attribute lands on the
// stage root), so the arrows and zoom controls stay usable while a photo loads.
const viewerCss = css`
  position: relative;
  height: 100%;

  [data-photo-ready] {
    transition: ${FADE};
  }

  /* zion paints its stage light gray and has no prop for it, so let the backdrop show through */
  [data-photo-ready] > [class*='stageBackgroundCss'] {
    background: transparent;
  }

  [data-photo-ready='false'] {
    opacity: 0;
  }

  /* zion draws Previous and Next as blue high-emphasis buttons with no color prop; white with a gray arrow sits better on the dark backdrop */
  [class*='navButton'] > button {
    --button-text-color: var(--gray80);
    --button-background: var(--gray00);
    --button-hover-background: var(--gray05);
    --button-active-background: var(--gray10);
  }

  @media (prefers-reduced-motion: reduce) {
    [data-photo-ready] {
      transition: none;
    }
  }
`

const footerCss = css`
  display: flex;
  justify-content: center;
  padding: 12px;
`

const preload = (src) => {
  if (!src) return
  const image = new window.Image()
  image.src = src
}

export default function MemoriesImageViewer({ overlay, photos, focusedIndex, setFocusedIndex, isAccessible }) {
  const [t] = useTranslation()
  // Keyed by src: a photo that has loaded (or failed) once does not show the spinner again
  const [settled, setSettled] = useState({})

  const images = useMemo(
    () =>
      photos.map((photo, index) => ({
        id: photo.id,
        src: photo.full,
        alt: t('person-details.photo-of-total', 'Photo {number} of {total}', {
          number: index + 1,
          total: photos.length,
        }),
      })),
    [photos, t]
  )

  const currentSrc = photos[focusedIndex]?.full
  const isSettled = Boolean(settled[currentSrc])

  const markSettled = useCallback((src) => {
    setSettled((previous) => (previous[src] ? previous : { ...previous, [src]: true }))
  }, [])

  // The viewer drops a load that finishes after the image changed, so onLoad is always the current photo
  const handleLoad = useCallback(() => markSettled(currentSrc), [currentSrc, markSettled])
  const handleError = useCallback(({ index }) => markSettled(photos[index]?.full), [markSettled, photos])

  // The viewer mounts only the focused image, so warm the cache for the photos either side
  useEffect(() => {
    if (!overlay.isOpen) return
    preload(photos[focusedIndex - 1]?.full)
    preload(photos[focusedIndex + 1]?.full)
  }, [overlay.isOpen, photos, focusedIndex])

  const title = images[focusedIndex]?.alt

  return (
    <FullPageOverlay
      {...overlay}
      title={title}
      footer={
        isAccessible ? (
          <div css={footerCss}>
            <Button emphasis="medium" onClick={overlay.close}>
              {t('person-details.close-photo', 'Close')}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div css={stageCss}>
        {photos[focusedIndex]?.thumbnail && (
          <img
            css={thumbnailCss}
            data-hidden={isSettled ? 'true' : undefined}
            src={photos[focusedIndex].thumbnail}
            alt=""
          />
        )}
        {!isSettled && (
          <output css={spinnerCss}>
            <NoticeLoading size="lg" aria-label={t('person-details.loading-image', 'Loading image')} />
          </output>
        )}
        <div css={viewerCss}>
          <MultiImageViewer
            images={images}
            focusedIndex={focusedIndex}
            setFocusedIndex={setFocusedIndex}
            downloadControl={false}
            onLoad={handleLoad}
            onError={handleError}
            data-photo-ready={isSettled ? 'true' : 'false'}
          />
        </div>
      </div>
    </FullPageOverlay>
  )
}

MemoriesImageViewer.propTypes = {
  overlay: shape({ isOpen: bool, close: func }).isRequired,
  photos: arrayOf(shape({ id: string, thumbnail: string, full: string })).isRequired,
  focusedIndex: number.isRequired,
  setFocusedIndex: func.isRequired,
  isAccessible: bool,
}
